import { useState } from "react";
import type { ReactNode } from "react";
import { CalendarDays, CalendarRange } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { AnalyticsBucket, AnalyticsDetail, AnalyticsProgress, AnalyticsSummaryRow } from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { AppIcon } from "../components/AppIcon.tsx";
import { Flag } from "../components/CountryPicker.tsx";
import { MenuSelect } from "../components/MenuSelect.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Card, Empty, ErrorState, Tile } from "../components/Ui.tsx";

/**
 * Kullanım: kaç cihazda kurulu, günlük aktif, ülke/sürüm/kanal dağılımı.
 *
 * Veri uygulamaların günde bir attığı anonim /ping'den gelir (kurulum kimliği +
 * sürüm + kanal; ülke Cloudflare'den, IP saklanmaz). "Kurulu" son 90 günde en az
 * bir kez açılan cihazdır: Play'in "yükleme" sayısından düşük çıkar, çünkü açılmayan
 * ya da kaldırılan cihazı saymaz. Mağaza tarafı için Play Console İstatistikleri.
 */

const RANGES = [30, 90, 365] as const;
type Range = (typeof RANGES)[number];

const nf = new Intl.NumberFormat("tr-TR");
const regionName = (() => {
  try {
    return new Intl.DisplayNames(["tr"], { type: "region" });
  } catch {
    return null;
  }
})();
const languageName = (() => {
  try {
    return new Intl.DisplayNames(["tr"], { type: "language" });
  } catch {
    return null;
  }
})();

/** "TR" → 🇹🇷 (bölge göstergesi harfleri). */
function flag(code: string): string {
  return /^[A-Z]{2}$/.test(code)
    ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : "";
}

export function AnalyticsPage(): ReactNode {
  const [params, setParams] = useSearchParams();
  const [range, setRange] = useState<Range>(30);

  const summary = useQuery({
    queryKey: ["analytics-summary"],
    queryFn: () => client.analyticsSummary(),
    refetchInterval: 60_000,
  });

  const apps = summary.data ?? [];
  const selected = params.get("app") ?? apps[0]?.appId ?? null;

  const detail = useQuery({
    queryKey: ["analytics", selected, range],
    queryFn: () => client.analytics(selected!, range),
    enabled: Boolean(selected),
    refetchInterval: 60_000,
  });

  const progress = useQuery({
    queryKey: ["analytics-progress", selected],
    queryFn: () => client.analyticsProgress(selected!),
    enabled: Boolean(selected),
    refetchInterval: 60_000,
  });

  const current = apps.find((a) => a.appId === selected);

  return (
    <>
      <PageHeader
        kicker="yönetim"
        title="Kullanım"
        meta="Uygulamaların günde bir attığı anonim sinyalden · ülke Cloudflare'den, IP saklanmaz"
        actions={
          <MenuSelect
            label="Zaman aralığı"
            icon={CalendarRange}
            panelWidth={180}
            value={String(range)}
            onChange={(v) => setRange(Number(v) as Range)}
            options={RANGES.map((r) => ({ value: String(r), label: `son ${r} gün` }))}
          />
        }
      />

      <PageBody>
        {summary.isError ? (
          <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
        ) : (
          <Card title="Uygulamalar" hint="son 90 günde açılan cihaz = kurulu" flush>
            {summary.isLoading ? (
              <Empty>Yükleniyor…</Empty>
            ) : (
              apps.map((a) => (
                <AppRow
                  key={a.appId}
                  row={a}
                  active={a.appId === selected}
                  onSelect={() => setParams({ app: a.appId }, { replace: true })}
                />
              ))
            )}
          </Card>
        )}

        {current && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Tile label="Bugün aktif" value={nf.format(current.active1d)} note="son 24 saat" />
              <Tile label="7 gün aktif" value={nf.format(current.active7d)} tone="ok" />
              <Tile label="30 gün aktif" value={nf.format(current.active30d)} tone="ok" />
              <Tile label="Kurulu" value={nf.format(current.installs)} tone="idle" note="son 90 gün" />
              <Tile label="Yeni" value={nf.format(current.new7d)} tone="warn" note="son 7 gün" />
            </div>

            {detail.isError ? (
              <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
            ) : detail.data ? (
              <Detail data={detail.data} />
            ) : (
              <Empty>Yükleniyor…</Empty>
            )}

            {/* Yalnızca ilerleme gönderen uygulamalar (oyunlar). */}
            {progress.data && progress.data.summary.players > 0 && <Progress data={progress.data} />}
          </>
        )}
      </PageBody>
    </>
  );
}

function AppRow({
  row,
  active,
  onSelect,
}: {
  row: AnalyticsSummaryRow;
  active: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`row w-full flex-wrap items-center gap-3 text-left transition hover:bg-surface-2 ${active ? "bg-surface-2" : ""}`}
      aria-pressed={active}
    >
      <AppIcon id={row.appId} name={row.displayName} size={22} />
      <span className={`min-w-28 flex-1 ${active ? "text-neutral-100" : "text-neutral-300"}`}>
        {row.displayName}
      </span>
      <Stat label="bugün" value={row.active1d} />
      <Stat label="30 gün" value={row.active30d} />
      <Stat label="kurulu" value={row.installs} muted />
    </button>
  );
}

function Stat({ label, value, muted = false }: { label: string; value: number; muted?: boolean }): ReactNode {
  return (
    <span className="grid w-16 justify-items-end gap-0.5">
      <span className={`mono text-md tabular-nums ${muted ? "text-neutral-500" : "text-neutral-100"}`}>
        {nf.format(value)}
      </span>
      <span className="text-2xs text-neutral-600">{label}</span>
    </span>
  );
}

function Detail({ data }: { data: AnalyticsDetail }): ReactNode {
  const hasData = data.daily.some((d) => d.active > 0);
  return (
    <>
      <Card title="Günlük aktif cihaz" hint={`son ${data.days} gün · TR günü`}>
        {hasData ? (
          <DailyChart daily={data.daily} />
        ) : (
          <Empty>
            Henüz sinyal yok. Uygulamalar sinyali içeren OTA'yı alıp açıldıkça dolmaya başlar.
          </Empty>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <CountryBreakdown data={data} />
        <Breakdown title="Sürüm" rows={data.versions} label={(k) => (k ? `v${k}` : "bilinmiyor")} mono />
        <Breakdown title="Kanal" rows={data.channels} label={(k) => k ?? "bilinmiyor"} mono />
        <Breakdown
          title="Dil"
          rows={data.locales}
          label={(k) => (k ? `${languageName?.of(k) ?? k} (${k})` : "bilinmiyor")}
        />
        <Breakdown title="Platform" rows={data.platforms} label={(k) => k ?? "bilinmiyor"} mono />
      </div>
    </>
  );
}

// --- Oyuncular nerede (oyunların ilerleme özeti) -------------------------

/** Oyunun kendi bölge/ırk/sınıf kimlikleri → konsolda okunur ad. Bilinmeyen kimlik olduğu gibi. */
const NAMES: Record<string, string> = {
  // Cinderpath (src/i18n/tr.ts)
  "misty-fields": "Sisli Tarlalar",
  "broken-mine": "Kırık Maden",
  "ash-forest": "Kül Ormanı",
  "sunken-keep": "Batık Kale",
  "frozen-march": "Donmuş Yürüyüş",
  "obsidian-spire": "Obsidyen Kule",
  "hollow-throne": "Boş Taht",
  human: "İnsan",
  elf: "Elf",
  dragonborn: "Ejderdoğan",
  dwarf: "Cüce",
  warrior: "Savaşçı",
  archer: "Okçu",
  mage: "Büyücü",
  raider: "Yağmacı",
};
const nameOf = (id: string | null): string => (id ? (NAMES[id] ?? id) : "bilinmiyor");
const fmt = (n: number | null): string => (n === null ? "—" : nf.format(n));

function ago(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? "bugün" : `${days} gün önce`;
}

function Progress({ data }: { data: AnalyticsProgress }): ReactNode {
  const s = data.summary;
  const zoneMax = Math.max(1, ...data.zones.map((z) => z.players));
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Tile label="İlerleme gönderen" value={nf.format(s.players)} tone="idle" note="son 90 gün" />
        <Tile label="Oynuyor" value={nf.format(s.active)} tone="ok" note={`son ${data.quitAfterDays} gün açtı`} />
        <Tile label="Bıraktı" value={nf.format(s.quit)} tone="warn" note={`${data.quitAfterDays}+ gündür yok`} />
        <Tile label="Ortanca rekor" value={fmt(s.median)} note="kademe" />
        <Tile label="En yüksek" value={fmt(s.max)} note="kademe" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Bölge dağılımı" hint="oyuncunun rekor kademesinin bölgesi" flush>
          {data.zones.map((z) => (
            <div key={z.zone ?? "∅"} className="row relative items-center gap-3">
              <span aria-hidden className="absolute inset-y-0 left-0 bg-accent-500/10" style={{ width: `${(z.players / zoneMax) * 100}%` }} />
              <span className="relative min-w-0 flex-1 truncate text-neutral-200">
                {nameOf(z.zone)}
                <span className="mono ml-2 text-2xs text-neutral-500">
                  {z.minBest === z.maxBest ? z.minBest : `${z.minBest}–${z.maxBest}`}
                </span>
              </span>
              <span className="mono relative text-xs text-neutral-500 tabular-nums" title="son günlerde açmayan">
                {z.quit ? `${nf.format(z.quit)} bıraktı` : ""}
              </span>
              <span className="mono relative w-12 text-right text-neutral-100 tabular-nums">{nf.format(z.players)}</span>
            </div>
          ))}
        </Card>

        <Card title="Kurulumun kaçıncı gününde nerede" hint="o gün açanların ortanca rekoru" flush>
          {data.byAge.map((a) => (
            <div key={a.day} className="row items-center gap-3">
              <span className="min-w-0 flex-1 text-neutral-300">{a.day}. gün</span>
              <span className="mono text-xs text-neutral-500 tabular-nums">{a.players ? `${nf.format(a.players)} oyuncu` : ""}</span>
              <span className="mono w-16 text-right text-neutral-100 tabular-nums">{fmt(a.median)}</span>
            </div>
          ))}
        </Card>
      </div>

      <Card title="Irk ve sınıf" hint="ortanca / en yüksek rekor · ortalama doğuş" flush>
        {data.lineage.map((l) => (
          <div key={`${l.race}-${l.class}`} className="row flex-wrap items-center gap-x-3 gap-y-1">
            <span className="min-w-40 flex-1 truncate text-neutral-200">
              {nameOf(l.race)} · {nameOf(l.class)}
            </span>
            <span className="mono text-xs text-neutral-500 tabular-nums">{nf.format(l.players)} oyuncu</span>
            <span className="mono w-24 text-right text-neutral-100 tabular-nums">
              {fmt(l.median)} / {fmt(l.max)}
            </span>
            <span className="mono w-20 whitespace-nowrap text-right text-xs text-neutral-500 tabular-nums">{l.runs ?? "—"} doğuş</span>
          </div>
        ))}
      </Card>

      <Card title="En yüksek kademeler" hint="ilk 20 · anonim kurulum" flush>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] text-sm">
            <thead className="text-2xs text-neutral-600">
              <tr className="text-left">
                <th className="px-4 py-2 font-normal">#</th>
                <th className="px-2 py-2 text-right font-normal">rekor</th>
                <th className="px-2 py-2 text-right font-normal">şu an</th>
                <th className="px-2 py-2 font-normal">bölge</th>
                <th className="px-2 py-2 text-right font-normal">doğuş</th>
                <th className="px-2 py-2 font-normal">ırk · sınıf</th>
                <th className="px-2 py-2 font-normal">ülke</th>
                <th className="px-4 py-2 text-right font-normal">son açılış</th>
              </tr>
            </thead>
            <tbody>
              {data.top.map((t, i) => (
                <tr key={i} className="border-t border-neutral-900">
                  <td className="mono px-4 py-2 text-neutral-500">{i + 1}</td>
                  <td className="mono px-2 py-2 text-right text-neutral-100 tabular-nums">{nf.format(t.best)}</td>
                  <td className="mono px-2 py-2 text-right text-neutral-400 tabular-nums">{fmt(t.stage)}</td>
                  <td className="px-2 py-2 text-neutral-300">{nameOf(t.zone)}</td>
                  <td className="mono px-2 py-2 text-right text-neutral-400 tabular-nums">{fmt(t.runs)}</td>
                  <td className="px-2 py-2 text-neutral-400">
                    {nameOf(t.race)} · {nameOf(t.class)}
                  </td>
                  <td className="px-2 py-2 text-neutral-400">{t.country ? `${flag(t.country)} ${t.country}` : "—"}</td>
                  <td className="px-4 py-2 text-right text-neutral-500">{ago(t.lastSeen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

/** Aktif cihaz çubukları + yeni kurulum noktaları. Kütüphanesiz SVG. */
function DailyChart({ daily }: { daily: AnalyticsDetail["daily"] }): ReactNode {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...daily.map((d) => d.active));
  const W = 1000;
  const H = 180;
  const gap = daily.length > 120 ? 0 : 2;
  const bw = W / daily.length;
  const shown = hover ?? daily.length - 1;
  const point = daily[shown]!;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
        <span className="mono text-neutral-400">
          {new Date(`${point.day}T00:00:00Z`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
        </span>
        <span>
          <span className="mono text-md text-neutral-100 tabular-nums">{nf.format(point.active)}</span>{" "}
          <span className="text-neutral-500">aktif</span>
        </span>
        <span>
          <span className="mono text-md tabular-nums" style={{ color: "var(--color-warn)" }}>
            {nf.format(point.new)}
          </span>{" "}
          <span className="text-neutral-500">yeni</span>
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-44 w-full"
        role="img"
        aria-label="Günlük aktif cihaz grafiği"
        onMouseLeave={() => setHover(null)}
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={0} x2={W} y1={H * f} y2={H * f} stroke="var(--color-neutral-900)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        ))}
        {daily.map((d, i) => {
          const h = (d.active / max) * (H - 8);
          const nh = (d.new / max) * (H - 8);
          return (
            <g key={d.day} onMouseEnter={() => setHover(i)}>
              <rect x={i * bw} y={0} width={bw} height={H} fill="transparent" />
              <rect
                x={i * bw + gap / 2}
                y={H - h}
                width={Math.max(1, bw - gap)}
                height={h}
                fill={i === shown ? "var(--color-accent-300)" : "var(--color-accent-500)"}
                opacity={i === shown ? 1 : 0.75}
              />
              {d.new > 0 && (
                <rect x={i * bw + gap / 2} y={H - nh} width={Math.max(1, bw - gap)} height={Math.max(2, nh)} fill="var(--color-warn)" opacity={0.85} />
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-2xs text-neutral-600">
        <span>{daily[0]!.day}</span>
        <span>en yüksek {nf.format(max)}</span>
        <span>{daily[daily.length - 1]!.day}</span>
      </div>
    </div>
  );
}

const dayLabel = (day: string): string =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString("tr-TR", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    weekday: "short",
  });

const sum = (rows: AnalyticsBucket[]): number => rows.reduce((total, r) => total + r.count, 0);

/** Ülke: son 30 gün ya da seçilen tek gün (o gün açan cihazlar). */
function CountryBreakdown({ data }: { data: AnalyticsDetail }): ReactNode {
  const [day, setDay] = useState("");
  const selected = data.dailyCountries.find((d) => d.day === day);
  return (
    <Breakdown
      title="Ülke"
      hint={
        selected
          ? selected.estimated > 0
            ? "o gün açan · cihazın son ülkesi"
            : "o gün açan cihaz"
          : undefined
      }
      action={
        <MenuSelect
          compact
          label="Ülke için gün"
          icon={CalendarDays}
          panelWidth={240}
          value={selected ? day : ""}
          onChange={setDay}
          options={[
            { value: "", label: "son 30 gün", meta: nf.format(sum(data.countries)) },
            ...data.dailyCountries.map((d) => ({
              value: d.day,
              label: dayLabel(d.day),
              meta: nf.format(sum(d.countries)),
              group: "Gün · TR saati",
            })),
          ]}
        />
      }
      rows={selected ? selected.countries : data.countries}
      label={(k) =>
        k ? (
          <span className="inline-flex items-center gap-2">
            <Flag code={k} />
            {regionName?.of(k) ?? k}
          </span>
        ) : (
          "bilinmiyor"
        )
      }
    />
  );
}

function Breakdown({
  title,
  hint = "son 30 gün aktif",
  action,
  rows,
  label,
  mono = false,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
  rows: AnalyticsBucket[];
  label: (key: string | null) => ReactNode;
  mono?: boolean;
}): ReactNode {
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  return (
    <Card title={title} hint={hint} action={action} flush>
      {rows.length ? (
        rows.slice(0, 12).map((r) => {
          const pct = total ? (r.count / total) * 100 : 0;
          return (
            <div key={r.key ?? "∅"} className="row relative items-center gap-3">
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 bg-accent-500/10"
                style={{ width: `${pct}%` }}
              />
              <span className={`relative min-w-0 flex-1 truncate ${mono ? "mono text-xs" : ""} ${r.key ? "text-neutral-200" : "text-neutral-500"}`}>
                {label(r.key)}
              </span>
              <span className="mono relative text-xs text-neutral-500 tabular-nums">{pct.toFixed(0)}%</span>
              <span className="mono relative w-12 text-right text-neutral-100 tabular-nums">{nf.format(r.count)}</span>
            </div>
          );
        })
      ) : (
        <Empty>Veri yok</Empty>
      )}
    </Card>
  );
}
